import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User as UserIcon, Users as UsersIcon, Shield } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) {
    return (
      <Link
        to="/auth"
        data-testid="login-link"
        className="inline-flex items-center btn-shimmer bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest text-xs px-5 py-3 cut-corners transition-colors"
      >
        Login / Sign Up
      </Link>
    );
  }

  const initial = (user.gamertag || user.display_name || user.email || "?")[0].toUpperCase();

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-btn"
        className="flex items-center gap-2 border border-white/10 hover:border-[#D4AF37] px-2 py-1.5 cut-corners transition-colors"
      >
        <div className="w-7 h-7 rounded-full overflow-hidden bg-black ring-1 ring-[#D4AF37]/40 grid place-items-center">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.display_name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display text-xs gold-text">{initial}</span>
          )}
        </div>
        <span className="hidden sm:inline text-xs font-bold uppercase tracking-widest text-zinc-200 max-w-[120px] truncate">
          {user.gamertag || user.display_name}
        </span>
      </button>

      {open && (
        <div
          data-testid="user-menu-panel"
          className="absolute right-0 mt-2 w-56 bg-[#0A0A0A] border border-[#D4AF37]/30 cut-corners shadow-2xl z-50"
        >
          <Link to="/profile" onClick={() => setOpen(false)} data-testid="menu-profile"
            className="flex items-center gap-2 px-4 py-3 hover:bg-white/5 text-zinc-200">
            <UserIcon size={14} className="text-[#D4AF37]" /> My Profile
          </Link>
          <Link to="/teams" onClick={() => setOpen(false)} data-testid="menu-teams"
            className="flex items-center gap-2 px-4 py-3 hover:bg-white/5 text-zinc-200 border-t border-white/5">
            <UsersIcon size={14} className="text-[#D4AF37]" /> Teams
          </Link>
          {user.role === "admin" && (
            <Link to="/admin" onClick={() => setOpen(false)} data-testid="menu-admin"
              className="flex items-center gap-2 px-4 py-3 hover:bg-white/5 text-zinc-200 border-t border-white/5">
              <Shield size={14} className="text-[#D4AF37]" /> Admin
            </Link>
          )}
          <button onClick={handleLogout} data-testid="menu-logout"
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-white/5 text-zinc-200 border-t border-white/5">
            <LogOut size={14} className="text-[#D4AF37]" /> Logout
          </button>
        </div>
      )}
    </div>
  );
}
